

import React, { useContext, FC, ReactNode, useState, useEffect } from 'react';
import { User, UserRole } from '../types';
import { auth, db } from '../services/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { ref, onValue, update, get, remove } from 'firebase/database';

export interface AuthContextType {
  user: User | null;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  isInitialized: boolean;
}

const AuthContext = React.createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | undefined;

    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = undefined;
      }

      if (firebaseUser) {
        const userRef = ref(db, `users/${firebaseUser.uid}`);
        
        unsubscribeProfile = onValue(userRef, async (snapshot) => {
          if (snapshot.exists()) {
            const userData = snapshot.val();
            const isAdminEmail = firebaseUser.email?.toLowerCase() === 'admin@tagexpress.com';
            
            const updatesToSync: Partial<User> = {};

            // 1. Sync email verification status
            if (firebaseUser.emailVerified && !userData.isEmailVerified) {
                updatesToSync.isEmailVerified = true;
            }

            // 2. Auto-verify the main account status on first email verification
            if (firebaseUser.emailVerified && !userData.isVerified) {
                updatesToSync.isVerified = true;
            }
            
            // 4. Force admin role and verification if DB is out of sync
            if (isAdminEmail) {
                if (userData.role !== UserRole.Admin) updatesToSync.role = UserRole.Admin;
                if (!userData.isVerified) updatesToSync.isVerified = true;
                if (!userData.isEmailVerified) updatesToSync.isEmailVerified = true;
            }
            
            if (Object.keys(updatesToSync).length > 0) {
                console.log("Syncing auth state to DB:", updatesToSync);
                try {
                    // User has permission to write to their own node.
                    await update(ref(db, `users/${firebaseUser.uid}`), updatesToSync);

                    // Clean up pending verification link if we just verified the email
                    if (updatesToSync.isEmailVerified === true) {
                        const pendingLinkRef = ref(db, `users/${firebaseUser.uid}/pendingEmailVerification`);
                        const checkSnapshot = await get(pendingLinkRef);
                        if (checkSnapshot.exists()) {
                            await remove(pendingLinkRef);
                        }
                    }
                } catch (err) {
                    console.error("Failed to sync auth state to DB (permissions or network):", err);
                }
            }

            // Create final user object for context, merging DB data with latest auth state
            setUser({
              id: firebaseUser.uid,
              ...userData, // Start with DB data
              ...updatesToSync, // Apply synced updates locally immediately
              email: firebaseUser.email || userData.email, // Auth email is the source of truth
              isEmailVerified: isAdminEmail || firebaseUser.emailVerified, // Auth state is the source of truth
              isVerified: isAdminEmail || userData.isVerified || updatesToSync.isVerified,
              role: isAdminEmail ? UserRole.Admin : userData.role,
            });
            setIsInitialized(true);
          } else {
            console.log("Profile not found in RTDB. Bootstraping if necessary...");
            const isAdminEmail = firebaseUser.email?.toLowerCase() === 'admin@tagexpress.com';
            
            const bootstrapProfile: User = {
                id: firebaseUser.uid,
                name: firebaseUser.displayName || (isAdminEmail ? 'Admin' : 'User'),
                email: firebaseUser.email || '',
                city: '',
                countryCode: '+44',
                phone: '',
                role: isAdminEmail ? UserRole.Admin : UserRole.Client,
                isVerified: isAdminEmail ? true : false,
                isEmailVerified: isAdminEmail ? true : firebaseUser.emailVerified,
                isLocked: false,
                teCode: `TE-${firebaseUser.uid.substring(0, 6).toUpperCase()}`
            };

            // Set the user immediately so they don't get stuck on login
            setUser(bootstrapProfile);
            setIsInitialized(true);

            // Attempt to write the missing profile to RTDB so future reads work
            try {
                await update(ref(db, `users/${firebaseUser.uid}`), bootstrapProfile);
            } catch (err) {
                console.error("Failed to bootstrap missing profile (might be due to signup race or rules):", err);
            }
          }
        }, (error) => {
            console.error("Error listening to user profile:", error);
            setUser(null);
            setIsInitialized(true);
        });
      } else {
        setUser(null);
        setIsInitialized(true);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

  const logout = async () => {
    await signOut(auth);
  };

  const updateUser = (updates: Partial<User>) => {
    setUser(prevUser => (prevUser ? { ...prevUser, ...updates } : null));
  };

  const value = {
    user,
    logout,
    updateUser,
    isInitialized
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
